import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { Frame } from '../components/Frame';
import { CenteredContentContainer } from '../components/layout/content/CenteredContentContainer';
import { HeaderWithLeftLogoAndControls } from '../components/layout/headers/HeaderWithLeftLogoAndControls';
import { PageLayout } from '../components/layout/PageLayout';
import { showRecordingPlayerPopup } from '../components/popups/recording/RecordingPlayerPopup';
import { RecordingsList } from '../components/RecordingsList';
import { BroadcastStateService } from '../services/broadcaststate/BroadcastStateService';
import { RECORDING_KEY_LINK_PARAM, RECORDING_LINK_PARAM, } from '../services/recording/RecordingPlaybackService';
import { RecordingService } from '../services/recording/RecordingService';
import { StreamrAppService } from '../services/streamrapp/StreamrAppService';
export const RecordingsListPage = () => {
    const activeBroadcast = BroadcastStateService.getOutput().useActiveBroadcast();
    const recordingService = RecordingService.getNotificationInterface();
    const recordingsList = RecordingService.getOutput().useRecordingsList();
    const location = useLocation();
    const openedFromLink = useRef(undefined);
    useEffect(() => {
        if (activeBroadcast === undefined) {
            // Cold load of a shared /recordings link: resolve the broadcast
            // from the URL. The page needs no engine: recordings are read
            // from storage and played by the recording service worker.
            StreamrAppService.getNotificationInterface().notifyResolveBroadcastFromUrl();
        }
    }, [activeBroadcast]);
    // a link to one recording opens it, once the list (or the key in the
    // link) tells what it is
    useEffect(() => {
        const recordingId = new URLSearchParams(location.search).get(RECORDING_LINK_PARAM);
        if (!activeBroadcast ||
            !recordingId ||
            openedFromLink.current === recordingId ||
            recordingsList?.status !== 'loaded') {
            return;
        }
        const indexKeyFromLink = new URLSearchParams(location.hash.replace(/^#/, '')).get(RECORDING_KEY_LINK_PARAM) ?? undefined;
        const listed = recordingsList.recordings.find((r) => r.recordingId === recordingId);
        if (!listed && !indexKeyFromLink) {
            return;
        }
        openedFromLink.current = recordingId;
        showRecordingPlayerPopup({
            recording: listed ?? {
                recordingId,
                streamId: activeBroadcast.streamId,
                broadcasterUserId: activeBroadcast.broadcasterUserId,
                timestamp: Date.now(),
                isPublic: false,
                isReady: true,
                durationUs: 0,
                contentLength: 0,
                partCount: 1,
                hasThumbnail: false,
            },
            broadcast: activeBroadcast,
            indexKeyFromLink,
        });
    }, [activeBroadcast, recordingsList, location]);
    useEffect(() => {
        if (activeBroadcast) {
            recordingService.notifyFetchRecordingsList(activeBroadcast.broadcasterUserId, activeBroadcast.streamId, activeBroadcast.myRole === 'broadcaster');
        }
        return () => {
            recordingService.notifyResetRecordingsList();
        };
    }, [activeBroadcast, recordingService]);
    if (activeBroadcast === undefined) {
        return null;
    }
    return (_jsx(PageLayout, { header: _jsx(HeaderWithLeftLogoAndControls, {}), children: _jsx(CenteredContentContainer, { children: _jsx(RecordingsListFrameRoot, { children: _jsxs(RecordingsListFrame, { children: [_jsx(RecordingsHeader, { children: _jsxs("span", { children: [activeBroadcast.name, " recordings"] }) }), recordingsList && (_jsx(RecordingsList, { recordings: recordingsList.recordings, variant: "broadcaster", hasMore: recordingsList.hasMore, status: recordingsList.status, onLoadMore: () => {
                                recordingService.notifyFetchMoreRecordings();
                            } }))] }) }) }) }));
};
const RecordingsListFrameRoot = styled.div `
    margin: 0 auto;
    max-width: 100%;
    padding: 0 1.75rem;
    width: var(--BroadcastFrame_Width, 90vw);
    box-sizing: border-box;

    @media (min-width: 80rem) {
        --BroadcastFrame_Width: 70vw;
    }
`;
const RecordingsListFrame = styled(Frame) `
    color: #ffffff;
    margin: 0 auto;
    min-width: 0;
    padding: 2rem;
    --tv-ui-shiny-border-radius: 2rem;
`;
const RecordingsHeader = styled.div `
    margin-bottom: 1rem;
    font-size: 1.35rem;
    font-weight: 500;
    width: 100%;
    text-align: left;
`;
